import { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Download, Upload, Pencil, PencilOff, Trash2, Check, X } from "lucide-react";
import { ColDef, ICellRendererParams, CellValueChangedEvent } from "ag-grid-community";

import { useAuth } from "@/contexts/AuthContext";
import { usePagination } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGDataGrid } from "@/components/ui/ag-data-grid";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { uomService, profileService, type ServiceContext, type UoMFilters } from "@/services";
import UoMFormDialog, { UoMRecord } from "./UoMFormDialog";
import UoMImportWizard, { UoMImportRow } from "./UoMImportWizard";
import UoMExportWizard from "./UoMExportWizard";

type FilterValue = "all" | "true" | "false";

export interface UoMTableRow {
  id: string;
  uom: string;
  singular: string;
  plural: string;
  description: string | null;
  is_si: boolean;
  is_imperial: boolean;
  is_active: boolean;
  company_id: string;
  created?: string | null;
  modified?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_by_profile?: { full_name: string | null } | null;
  updated_by_profile?: { full_name: string | null } | null;
}

export default function UoMManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Pagination hook with URL persistence disabled (inside Configuration tab)
  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    resetToFirstPage,
    pageSizeOptions,
  } = usePagination({
    defaultPageSize: 25,
    storageKey: 'uom',
    persistToUrl: false,
  });

  const [filterSI, setFilterSI] = useState<FilterValue>("all");
  const [filterImperial, setFilterImperial] = useState<FilterValue>("all");
  const [filterActive, setFilterActive] = useState<FilterValue>("all");

  const [isEditMode, setIsEditMode] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedUoM, setSelectedUoM] = useState<UoMRecord | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<Record<string, unknown> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uomToDelete, setUomToDelete] = useState<UoMTableRow | null>(null);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [exportWizardOpen, setExportWizardOpen] = useState(false);

  // Fetch user profile to get company_id using service
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  const getServiceContext = useCallback((): ServiceContext => ({
    companyId: companyId!,
    userId: user?.id,
  }), [companyId, user?.id]);

  // Build filters object for service
  const filters: UoMFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    isSI: filterSI === "all" ? null : filterSI === "true",
    isImperial: filterImperial === "all" ? null : filterImperial === "true",
    isActive: filterActive === "all" ? null : filterActive === "true",
  }), [debouncedSearch, filterSI, filterImperial, filterActive]);

  // Fetch paginated UoM records
  const { data: paginatedResult, isLoading, isFetching } = useQuery({
    queryKey: ["uom", companyId, pageIndex, pageSize, debouncedSearch, filterSI, filterImperial, filterActive],
    queryFn: async () => {
      if (!companyId) return { data: [], totalCount: 0, error: null };
      const result = await uomService.listPaginated(
        companyId,
        { page: pageIndex, pageSize },
        filters
      );
      if (result.error) throw result.error;
      return result;
    },
    enabled: !!companyId,
    placeholderData: (previousData) => previousData,
  });

  // Fetch all UoM records for export (only when export wizard is open)
  const { data: allRecordsResult } = useQuery({
    queryKey: ["uom-all", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const result = await uomService.list(companyId);
      if (result.error) throw result.error;
      return result.data as UoMRecord[];
    },
    enabled: !!companyId && exportWizardOpen,
  });

  const hasActiveFilter =
    debouncedSearch !== "" || filterSI !== "all" || filterImperial !== "all" || filterActive !== "all";

  // ============================================================================
  // AG Grid Cell Value Changed Handler
  // ============================================================================

  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent<UoMTableRow>) => {
    const { data: rowData, colDef, oldValue, newValue } = event;
    if (!rowData || !companyId || oldValue === newValue) return;

    const columnId = colDef.field as keyof UoMTableRow;
    
    // Handle null for description column
    const finalNewValue = columnId === "description" && newValue === "" ? null : newValue;

    // Build snapshot with just the changed field
    const snapshot = { [columnId]: oldValue };
    const updateData = { [columnId]: finalNewValue };

    // Optimistic update already applied by AG Grid
    try {
      const result = await uomService.update(rowData.id, updateData, snapshot, getServiceContext());
      if (result.error) throw result.error;
      // Silently succeed - no toast for inline edits
    } catch (error) {
      // Rollback by refetching data
      queryClient.invalidateQueries({ queryKey: ["uom", companyId] });
      toast.error("Failed to update");
      console.error(error);
    }
  }, [companyId, queryClient, getServiceContext]);

  // ============================================================================
  // AG Grid Column Definitions
  // ============================================================================

  const columnDefs = useMemo<ColDef<UoMTableRow>[]>(() => [
    {
      field: "uom",
      headerName: "UoM",
      width: 100,
      sortable: true,
      editable: isEditMode,
      cellClass: "font-medium",
    },
    {
      field: "singular",
      headerName: "Singular",
      flex: 1,
      minWidth: 120,
      sortable: true,
      editable: isEditMode,
    },
    {
      field: "plural",
      headerName: "Plural",
      flex: 1,
      minWidth: 120,
      sortable: true,
      editable: isEditMode,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 200,
      sortable: true,
      editable: isEditMode,
      valueFormatter: (params) => params.value || "—",
      cellClass: "text-muted-foreground",
    },
    {
      field: "is_si",
      headerName: "SI",
      width: 80,
      sortable: true,
      editable: isEditMode,
      cellEditor: "agCheckboxCellEditor",
      cellRenderer: (params: ICellRendererParams<UoMTableRow>) =>
        params.value ? (
          <Check className="w-4 h-4 text-primary mx-auto" />
        ) : (
          <X className="w-4 h-4 text-muted-foreground mx-auto" />
        ),
      cellClass: "text-center",
      headerClass: "ag-header-center",
    },
    {
      field: "is_imperial",
      headerName: "Imperial",
      width: 90,
      sortable: true,
      editable: isEditMode,
      cellEditor: "agCheckboxCellEditor",
      cellRenderer: (params: ICellRendererParams<UoMTableRow>) =>
        params.value ? (
          <Check className="w-4 h-4 text-primary mx-auto" />
        ) : (
          <X className="w-4 h-4 text-muted-foreground mx-auto" />
        ),
      cellClass: "text-center",
      headerClass: "ag-header-center",
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 80,
      sortable: true,
      editable: isEditMode,
      cellEditor: "agCheckboxCellEditor",
      cellRenderer: (params: ICellRendererParams<UoMTableRow>) =>
        params.value ? (
          <Check className="w-4 h-4 text-primary mx-auto" />
        ) : (
          <X className="w-4 h-4 text-muted-foreground mx-auto" />
        ),
      cellClass: "text-center",
      headerClass: "ag-header-center",
    },
    {
      colId: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams<UoMTableRow>) => (
        <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => params.data && handleDeleteClick(params.data)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ], [isEditMode]);

  // Row click handler for read-only mode (opens edit dialog)
  const handleRowClick = useCallback((row: UoMTableRow) => {
    if (isEditMode) return;
    
    setSelectedSnapshot({
      uom: row.uom,
      singular: row.singular,
      plural: row.plural,
      description: row.description,
      is_si: row.is_si,
      is_imperial: row.is_imperial,
      is_active: row.is_active,
    });
    setSelectedUoM({ ...row });
    setFormDialogOpen(true);
  }, [isEditMode]);

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  const handleNewClick = () => {
    setSelectedUoM(null);
    setSelectedSnapshot(null);
    setFormDialogOpen(true);
  };

  function handleDeleteClick(record: UoMTableRow) {
    setUomToDelete(record);
    setDeleteDialogOpen(true);
  }

  const handleConfirmDelete = async () => {
    if (!uomToDelete || !companyId) return;

    const snapshot = {
      uom: uomToDelete.uom,
      singular: uomToDelete.singular,
      plural: uomToDelete.plural,
      description: uomToDelete.description,
      is_si: uomToDelete.is_si,
      is_imperial: uomToDelete.is_imperial,
      is_active: uomToDelete.is_active,
    };

    try {
      const result = await uomService.delete(uomToDelete.id, snapshot, getServiceContext());
      if (result.error) throw result.error;

      queryClient.invalidateQueries({ queryKey: ["uom", companyId] });
      queryClient.invalidateQueries({ queryKey: ["uom-all", companyId] });
      toast.success("Unit of measure deleted");
    } catch (error) {
      toast.error("Failed to delete unit of measure");
      console.error(error);
    }

    setDeleteDialogOpen(false);
    setUomToDelete(null);
  };

  const handleSave = async (data: Omit<UoMRecord, "id">) => {
    if (!companyId) return;

    try {
      if (selectedUoM) {
        const result = await uomService.update(selectedUoM.id, data, selectedSnapshot || {}, getServiceContext());
        if (result.error) throw result.error;
        toast.success("Unit of measure updated");
      } else {
        const result = await uomService.create(data, getServiceContext());
        if (result.error) throw result.error;
        toast.success("Unit of measure created");
      }

      queryClient.invalidateQueries({ queryKey: ["uom", companyId] });
      queryClient.invalidateQueries({ queryKey: ["uom-all", companyId] });
    } catch (error) {
      toast.error(selectedUoM ? "Failed to update unit of measure" : "Failed to create unit of measure");
      console.error(error);
    }
  };

  const handleImport = async (records: UoMImportRow[]) => {
    if (!companyId || !user?.id) return;

    try {
      const context: ServiceContext = { companyId, userId: user.id };
      const toInsert = records.map((r) => ({
        uom: r.uom,
        singular: r.singular,
        plural: r.plural,
        description: r.description || null,
        is_si: r.is_si ?? false,
        is_imperial: r.is_imperial ?? false,
        is_active: r.is_active !== false,
      }));

      const result = await uomService.createBatch(toInsert, context);
      if (result.error) throw result.error;

      queryClient.invalidateQueries({ queryKey: ["uom", companyId] });
      queryClient.invalidateQueries({ queryKey: ["uom-all", companyId] });
      toast.success(`Imported ${records.length} units of measure`);
    } catch (error) {
      toast.error("Failed to import units of measure");
      console.error(error);
    }
  };

  // Handle filter changes - reset to first page
  const handleFilterChange = (setter: (value: FilterValue) => void) => (value: FilterValue) => {
    setter(value);
    resetToFirstPage();
  };

  if (!companyId) {
    return <div className="text-center py-12 text-muted-foreground">Please set up your company profile first.</div>;
  }

  // Prepare export data - apply current filters to all records if filters are active
  const allRecords = allRecordsResult || [];
  const filteredRecordsForExport = hasActiveFilter
    ? allRecords.filter((record) => {
        const matchesSearch =
          !debouncedSearch ||
          record.uom.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          record.singular.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          record.plural.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          (record.description?.toLowerCase().includes(debouncedSearch.toLowerCase()) ?? false);

        const matchesSI =
          filterSI === "all" || (filterSI === "true" && record.is_si) || (filterSI === "false" && !record.is_si);

        const matchesImperial =
          filterImperial === "all" ||
          (filterImperial === "true" && record.is_imperial) ||
          (filterImperial === "false" && !record.is_imperial);

        const matchesActive =
          filterActive === "all" ||
          (filterActive === "true" && record.is_active) ||
          (filterActive === "false" && !record.is_active);

        return matchesSearch && matchesSI && matchesImperial && matchesActive;
      })
    : allRecords;

  // Toolbar actions including filters
  const toolbarActions = (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Filters */}
      <Select value={filterSI} onValueChange={handleFilterChange(setFilterSI)}>
        <SelectTrigger className="w-[110px]">
          <SelectValue placeholder="SI" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All SI</SelectItem>
          <SelectItem value="true">SI Only</SelectItem>
          <SelectItem value="false">Non-SI</SelectItem>
        </SelectContent>
      </Select>

      <Select value={filterImperial} onValueChange={handleFilterChange(setFilterImperial)}>
        <SelectTrigger className="w-[120px]">
          <SelectValue placeholder="Imperial" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Imperial</SelectItem>
          <SelectItem value="true">Imperial Only</SelectItem>
          <SelectItem value="false">Non-Imperial</SelectItem>
        </SelectContent>
      </Select>

      <Select value={filterActive} onValueChange={handleFilterChange(setFilterActive)}>
        <SelectTrigger className="w-[110px]">
          <SelectValue placeholder="Active" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Status</SelectItem>
          <SelectItem value="true">Active</SelectItem>
          <SelectItem value="false">Inactive</SelectItem>
        </SelectContent>
      </Select>

      <Button variant="outline" size="sm" onClick={() => setExportWizardOpen(true)}>
        <Download className="w-4 h-4 mr-2" />
        Export
      </Button>
      <Button variant="outline" size="sm" onClick={() => setImportWizardOpen(true)}>
        <Upload className="w-4 h-4 mr-2" />
        Import
      </Button>
      <Button size="sm" onClick={handleNewClick}>
        <Plus className="w-4 h-4 mr-2" />
        New UoM
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* AG Grid with integrated toolbar */}
      <AGDataGrid<UoMTableRow>
        rowData={paginatedResult?.data || []}
        columnDefs={columnDefs}
        totalCount={paginatedResult?.totalCount || 0}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search UoM..."
        isLoading={isLoading && !paginatedResult}
        isFetching={isFetching}
        isEditMode={isEditMode}
        showEditToggle={true}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onEditModeChange={setIsEditMode}
        onCellValueChanged={handleCellValueChanged}
        onRowClicked={handleRowClick}
        toolbarActions={toolbarActions}
        height="calc(100vh - 400px)"
        minHeight="300px"
      />

      {/* Dialogs */}
      <UoMFormDialog open={formDialogOpen} onOpenChange={setFormDialogOpen} uom={selectedUoM} onSave={handleSave} />

      <UoMImportWizard open={importWizardOpen} onOpenChange={setImportWizardOpen} onImport={handleImport} />

      <UoMExportWizard
        open={exportWizardOpen}
        onOpenChange={setExportWizardOpen}
        allRecords={allRecords}
        filteredRecords={filteredRecordsForExport}
        hasActiveFilter={hasActiveFilter}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Unit of Measure</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{uomToDelete?.uom}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
